// Minimal typed fetch wrapper. All requests are same-origin and send cookies
// (needed for the admin session). API routes are namespaced under /api.

import { SESSION_EXPIRED, noteSessionActivity, reportSessionExpired } from '../shared/session';

export interface ApiError {
  success: false;
  error: string;
}

export class ApiRequestError extends Error {
  status: number;
  /** The parsed response body — structured refusals (e.g. `402 plan_limit`)
   *  carry the data the client renders from, not just a message. */
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    // headers must come AFTER ...init so the computed Content-Type isn't dropped
    // when a caller passes its own headers (e.g. the Auth header on submit).
    headers: {
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    /*
     * A dead session is not this caller's problem to render. The API marks the
     * one 401 that means it (a wrong current password on the change-password
     * form is a 401 too, and is very much the caller's problem), and the
     * signed-in shell answers it by going to the login screen — so a save that
     * cannot save says so on the screen it belongs on, instead of leaving
     * somebody editing into a session that ended hours ago.
     */
    if (res.status === 401 && data?.code === SESSION_EXPIRED) reportSessionExpired();
    const message = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new ApiRequestError(message, res.status, data);
  }
  // The server slides the idle window on every request it answers; this keeps
  // the client's watchdog counting from the same moment.
  noteSessionActivity();
  return data as T;
}
